/**
 * Cleeaura theme — cart drawer, mobile nav, product form, gallery
 * Uses native Shopify Cart AJAX API (compatible with Meta channel tracking)
 */
(function () {
  'use strict';

  const theme = window.theme || {};

  /* ---------- helpers ---------- */
  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }
  function qsa(sel, root) {
    return Array.from((root || document).querySelectorAll(sel));
  }
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = String(cents).replace('.', '');
    const num = Number(cents);
    if (Number.isNaN(num)) return '';
    const whole = Math.round(num / 100);
    const value = (num / 100).toFixed(2);
    const format = theme.moneyFormat || 'Rs {{amount}}';
    try {
      let out = format;
      if (out.includes('{{amount_no_decimals}}')) {
        out = out.replace('{{amount_no_decimals}}', String(whole));
      } else if (out.includes('{{amount_with_comma_separator}}')) {
        out = out.replace('{{amount_with_comma_separator}}', value.replace('.', ','));
      } else {
        out = out.replace('{{amount}}', value);
      }
      // Prefer clean PKR look: "Rs 5,499"
      if (/rs\.?\s*/i.test(out) && !out.includes(',')) {
        const withComma = whole.toLocaleString('en-PK');
        out = out.replace(/[\d.]+/, withComma).replace(/Rs\.?\s*/i, 'Rs ');
      }
      return out.replace(/Rs\.(?=\d)/i, 'Rs ').replace(/\s+/g, ' ').trim();
    } catch (e) {
      return 'Rs ' + whole.toLocaleString('en-PK');
    }
  }

  /* ---------- cart drawer ---------- */
  const CartDrawer = {
    el: null,
    body: null,
    footer: null,

    init() {
      this.el = qs('[data-cart-drawer]');
      if (!this.el) return;
      this.body = qs('[data-cart-drawer-body]', this.el);
      this.footer = qs('[data-cart-drawer-footer]', this.el);

      qsa('[data-cart-open]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      qsa('[data-cart-close]', this.el).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.el.classList.contains('is-open')) this.close();
      });
    },

    open() {
      if (!this.el) return;
      this.el.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      this.refresh();
    },

    close() {
      if (!this.el) return;
      this.el.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
    },

    async refresh() {
      try {
        const res = await fetch((theme.routes && theme.routes.cart_url ? theme.routes.cart_url : '/cart') + '.js', {
          headers: { Accept: 'application/json' }
        });
        const cart = await res.json();
        this.render(cart);
        this.updateBubbles(cart.item_count);
      } catch (err) {
        console.error('Cart refresh failed', err);
      }
    },

    updateBubbles(count) {
      qsa('[data-cart-count]').forEach((el) => {
        el.textContent = count > 0 ? String(count) : '';
        el.setAttribute('data-count', String(count || 0));
      });
    },

    render(cart) {
      if (!this.body) return;

      if (!cart.items || cart.items.length === 0) {
        this.body.innerHTML =
          '<div class="cart-drawer__empty"><p>Your cart is empty</p><a class="btn btn-primary" href="/collections/all" data-cart-close>Continue shopping</a></div>';
        if (this.footer) this.footer.hidden = true;
        return;
      }

      if (this.footer) this.footer.hidden = false;

      this.body.innerHTML = cart.items
        .map((item) => {
          const img = item.image
            ? `<img src="${item.image}" alt="${escapeHtml(item.product_title)}" width="72" height="72" loading="lazy">`
            : '';
          const variant =
            item.variant_title && item.variant_title !== 'Default Title'
              ? `<div class="cart-item__variant">${escapeHtml(item.variant_title)}</div>`
              : '';
          return `
            <div class="cart-item" data-key="${item.key}">
              <div class="cart-item__image">${img}</div>
              <div>
                <div class="cart-item__title">${escapeHtml(item.product_title)}</div>
                ${variant}
                <div class="cart-item__meta">
                  <div class="quantity-selector" data-qty-key="${item.key}">
                    <button type="button" data-qty-change="-1" aria-label="Decrease">−</button>
                    <input type="number" value="${item.quantity}" min="0" aria-label="Quantity" data-qty-input>
                    <button type="button" data-qty-change="1" aria-label="Increase">+</button>
                  </div>
                  <div class="cart-item__price">${formatMoney(item.final_line_price)}</div>
                  <button type="button" class="cart-item__remove" data-remove-key="${item.key}">Remove</button>
                </div>
              </div>
            </div>`;
        })
        .join('');

      const subtotalEl = qs('[data-cart-subtotal]', this.el);
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

      this.bindLineEvents();
    },

    bindLineEvents() {
      qsa('[data-qty-change]', this.body).forEach((btn) => {
        btn.addEventListener('click', async () => {
          const wrap = btn.closest('[data-qty-key]');
          const key = wrap.getAttribute('data-qty-key');
          const input = qs('[data-qty-input]', wrap);
          const delta = Number(btn.getAttribute('data-qty-change'));
          const next = Math.max(0, Number(input.value) + delta);
          input.value = next;
          await this.changeLine(key, next);
        });
      });

      qsa('[data-remove-key]', this.body).forEach((btn) => {
        btn.addEventListener('click', async () => {
          await this.changeLine(btn.getAttribute('data-remove-key'), 0);
        });
      });
    },

    async changeLine(key, quantity) {
      try {
        const res = await fetch(theme.routes.cart_change_url + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ id: key, quantity })
        });
        if (!res.ok) throw new Error('Change failed');
        const cart = await res.json();
        this.render(cart);
        this.updateBubbles(cart.item_count);
      } catch (err) {
        console.error(err);
        alert((theme.strings && theme.strings.cartError) || 'Could not update cart');
      }
    },

    async addFromForm(form, button) {
      const formData = new FormData(form);
      if (button) button.classList.add('is-loading');

      try {
        const res = await fetch(theme.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
        const data = await res.json();
        if (data.status && data.status !== 200) {
          throw new Error(data.description || data.message || 'Add failed');
        }
        await this.refresh();
        if (theme.cartType !== 'page') this.open();
      } catch (err) {
        console.error(err);
        alert(err.message || (theme.strings && theme.strings.cartError) || 'Could not add to cart');
      } finally {
        if (button) button.classList.remove('is-loading');
      }
    }
  };

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ---------- mobile nav ---------- */
  function initMobileNav() {
    const nav = qs('[data-mobile-nav]');
    const openBtn = qs('[data-menu-open]');
    if (!nav || !openBtn) return;

    const closeAll = () => {
      nav.classList.remove('is-open');
      openBtn.classList.remove('is-active');
      document.body.classList.remove('overflow-hidden');
      openBtn.setAttribute('aria-expanded', 'false');
    };

    openBtn.addEventListener('click', () => {
      const open = !nav.classList.contains('is-open');
      nav.classList.toggle('is-open', open);
      openBtn.classList.toggle('is-active', open);
      document.body.classList.toggle('overflow-hidden', open);
      openBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    qsa('[data-menu-close]', nav).forEach((el) => el.addEventListener('click', closeAll));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) closeAll();
    });
  }

  /* ---------- product form / variants / swatches ---------- */
  function initProductForms() {
    qsa('[data-product-form]').forEach((form) => {
      const root = form.closest('[data-product-root]') || document;
      const productJson = qs('[data-product-json]', root);
      let product = null;
      if (productJson) {
        try {
          product = JSON.parse(productJson.textContent);
        } catch (e) {
          product = null;
        }
      }

      const idInput = qs('[name="id"]', form);
      const priceEl = qs('[data-product-price]', root);
      const atc = qs('[type="submit"]', form);
      const optionSelects = qsa('[data-option-select]', form);
      const mainImg = qs('[data-gallery-main]', root);

      function getSelectedOptions() {
        return optionSelects.map((s) => s.value);
      }

      function findVariant() {
        if (!product || !product.variants) return null;
        const opts = getSelectedOptions();
        return product.variants.find((v) => opts.every((o, i) => v['option' + (i + 1)] === o));
      }

      function setSelectValue(select, value) {
        if (!select) return;
        select.value = value;
        Array.from(select.options).forEach((opt) => {
          opt.selected = opt.value === value;
        });
      }

      function syncSwatchUI() {
        optionSelects.forEach((select, index) => {
          const wrap = select.closest('.product-option');
          if (!wrap) return;
          const label = qs('[data-option-value-label]', wrap);
          if (label) label.textContent = select.value;
          qsa('[data-swatch]', wrap).forEach((btn) => {
            const on = btn.getAttribute('data-value') === select.value;
            btn.classList.toggle('is-selected', on);
            btn.setAttribute('aria-selected', on ? 'true' : 'false');
          });
        });
      }

      function updateMedia(variant) {
        if (!mainImg || !variant || !product || !product.media) return;
        let media = null;
        if (variant.featured_media) {
          media = product.media.find((m) => m.id === variant.featured_media.id);
        }
        if (!media && variant.featured_image) {
          media = product.media.find((m) => m.src && variant.featured_image.src && m.src === variant.featured_image.src);
        }
        if (!media) return;
        // Prefer full size from matching thumb if present
        const thumb = qs('[data-gallery-thumb][data-media-id="' + media.id + '"]', root);
        if (thumb) {
          const src = thumb.getAttribute('data-full-src');
          if (src) {
            mainImg.src = src;
            mainImg.srcset = '';
          }
          qsa('[data-gallery-thumb]', root).forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        }
      }

      function updateVariant() {
        syncSwatchUI();
        const variant = findVariant();
        if (!variant) {
          if (idInput) idInput.value = '';
          if (atc) {
            atc.disabled = true;
            atc.textContent = (theme.strings && theme.strings.unavailable) || 'Unavailable';
          }
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (atc) {
          atc.disabled = !variant.available;
          atc.textContent = variant.available
            ? (theme.strings && theme.strings.addToCart) || 'Add to cart'
            : (theme.strings && theme.strings.soldOut) || 'Sold out';
        }
        if (priceEl) {
          let html = '<span class="price-item price-item--regular">' + formatMoney(variant.price) + '</span>';
          if (variant.compare_at_price > variant.price) {
            html =
              '<span class="price-item price-item--sale">' +
              formatMoney(variant.price) +
              '</span> <s class="price-item price-item--compare">' +
              formatMoney(variant.compare_at_price) +
              '</s>';
          }
          priceEl.innerHTML = html;
        }
        updateMedia(variant);
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) {}
      }

      // Swatch / pill clicks
      qsa('[data-swatch]', form).forEach((btn) => {
        btn.addEventListener('click', () => {
          const index = Number(btn.getAttribute('data-option-index'));
          const value = btn.getAttribute('data-value');
          const select = optionSelects[index];
          if (!select) return;
          setSelectValue(select, value);
          updateVariant();
        });
      });

      optionSelects.forEach((s) => s.addEventListener('change', updateVariant));
      if (optionSelects.length) updateVariant();
      else syncSwatchUI();

      form.addEventListener('submit', (e) => {
        if (theme.cartType === 'page') return;
        e.preventDefault();
        CartDrawer.addFromForm(form, atc);
      });
    });
  }

  /* ---------- quantity buttons (product page) ---------- */
  function initQuantity() {
    qsa('[data-qty-wrapper]').forEach((wrap) => {
      const input = qs('input', wrap);
      qsa('[data-qty-btn]', wrap).forEach((btn) => {
        btn.addEventListener('click', () => {
          const delta = Number(btn.getAttribute('data-qty-btn'));
          const min = Number(input.min || 1);
          const next = Math.max(min, Number(input.value || 1) + delta);
          input.value = next;
        });
      });
    });
  }

  /* ---------- gallery ---------- */
  function initGallery() {
    qsa('[data-product-gallery]').forEach((gallery) => {
      const main = qs('[data-gallery-main]', gallery);
      const thumbs = qsa('[data-gallery-thumb]', gallery);
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const src = thumb.getAttribute('data-full-src');
          if (main && src) {
            main.src = src;
            main.srcset = '';
          }
          thumbs.forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    });
  }

  /* ---------- reveal on scroll ---------- */
  function initReveal() {
    const items = qsa('[data-reveal]');
    if (!items.length || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    items.forEach((el) => io.observe(el));
  }

  document.addEventListener('DOMContentLoaded', () => {
    theme.cartType =
      document.body.getAttribute('data-cart-type') ||
      document.documentElement.getAttribute('data-cart-type') ||
      'drawer';
    CartDrawer.init();
    initMobileNav();
    initProductForms();
    initQuantity();
    initGallery();
    initReveal();

    // Initial cart count
    fetch((theme.routes && theme.routes.cart_url ? theme.routes.cart_url : '/cart') + '.js')
      .then((r) => r.json())
      .then((cart) => CartDrawer.updateBubbles(cart.item_count))
      .catch(() => {});
  });

  window.CleeauraCart = CartDrawer;
})();
